/** react-query hooks for /api/portal (customer portal staff controls). Keys all start with ['portal']. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/auth/AuthContext';

export interface SyncBrief {
  id: string;
  status: 'running' | 'ok' | 'partial' | 'error' | 'refused';
  startedAt: string;
  finishedAt: string | null;
  counts: { customers: number; invoices: number; payments: number; credits: number; indexEntries: number };
  errors: string[];
}

export type PortalStatus =
  | { configured: false }
  | {
      configured: true;
      database: string;
      enabled: boolean;
      message: { en: string; es: string } | null;
      updatedBy: string | null;
      updatedAt: string | null;
      blockCount: number;
      activeSessions: number;
      signIns24h: number;
      signInsTruncated: boolean;
      lastSync: SyncBrief | null;
      lastGoodSync: SyncBrief | null;
    };

export interface PortalBlock {
  hash: string;
  maskedEmail: string;
  reason: string;
  by: string;
  at: string;
}

export interface PortalAddress {
  email: string;
  maskedEmail: string;
  sources: string[];
  domainBlocked: boolean;
  removedByStaff: boolean;
  blocked: boolean;
  canSignIn: boolean;
}

/** An address the live index opens this account with that the list above does not show (masked only). */
export interface PortalOtherAddress {
  maskedEmail: string;
  sources: string[];
  /** How many OTHER customers the same address opens. */
  sharedWith: number;
  blocked: boolean;
}

export interface PortalAccount {
  inPortal: boolean;
  lastLogin: { at: string; maskedEmail: string | null } | null;
  addresses: PortalAddress[];
  otherAddresses?: PortalOtherAddress[];
  contacts: { add: Array<{ email: string; by: string; at: string }>; remove: Array<{ email: string; by: string; at: string }> };
}

export interface PortalEvent {
  id: string;
  at: string;
  type: string;
  maskedEmail: string | null;
  outcome: 'ok' | 'denied' | 'error';
  ipPrefix: string | null;
  uaFamily: string | null;
  detail: string | null;
}

export const portalKeys = {
  all: ['portal'] as const,
  status: ['portal', 'status'] as const,
  blocks: ['portal', 'blocks'] as const,
  account: (id: string) => ['portal', 'account', id] as const,
  events: (id: string) => ['portal', 'events', id] as const,
};

/** One cheap query shared by the sidebar, the page and the account card: `configured: false` hides every portal surface. */
export function usePortalStatus() {
  const api = useApi();
  return useQuery({
    queryKey: portalKeys.status,
    staleTime: 30_000,
    retry: false,
    queryFn: () => api.get<PortalStatus>('/api/portal/status'),
  });
}

/** True only when the BFF says the portal database is set up. Errors and loading read as false. */
export function usePortalConfigured(): boolean {
  const q = usePortalStatus();
  return q.data?.configured === true;
}

export function useBlocks(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: portalKeys.blocks,
    enabled,
    queryFn: () => api.get<{ blocks: PortalBlock[] }>('/api/portal/blocks').then((r) => r.blocks),
  });
}

export function useAccountPortal(id: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: portalKeys.account(id),
    enabled,
    queryFn: () => api.get<PortalAccount>(`/api/portal/accounts/${encodeURIComponent(id)}`),
  });
}

export function usePortalEvents(id: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: portalKeys.events(id),
    enabled,
    queryFn: () => api.get<{ events: PortalEvent[] }>(`/api/portal/events?accountId=${encodeURIComponent(id)}&limit=15`).then((r) => r.events),
  });
}

/** A mutation that refreshes every portal query afterwards. */
function usePortalMutation<TBody, TResult>(run: (api: ReturnType<typeof useApi>, body: TBody) => Promise<TResult>) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => run(api, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: portalKeys.all }),
  });
}

export const useSaveKillSwitch = () =>
  usePortalMutation((api, b: { enabled: boolean; reason: string; confirm: string; message?: { en: string; es: string } | null }) => api.put('/api/portal/settings', b));
export const useAddBlock = () => usePortalMutation((api, b: { email: string; reason: string }) => api.post('/api/portal/blocks', b));
export const useRemoveBlock = () => usePortalMutation((api, hash: string) => api.delete(`/api/portal/blocks/${encodeURIComponent(hash)}`));
export const useRevokeSessions = () =>
  usePortalMutation((api, b: { email: string; confirm: string } | { all: true; confirm: string; reason: string }) => api.post<{ sessionsRevoked: number }>('/api/portal/sessions/revoke', b));
export const useSaveContact = (accountId: string) =>
  usePortalMutation((api, b: { action: 'add' | 'remove' | 'restore'; email: string }) => api.put(`/api/portal/accounts/${encodeURIComponent(accountId)}/contacts`, b));
